import { Box, Button, Container, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import useGlobal from "./useGlobal";
import './inventur.css';

export function Inventur() {
    const [axios] = useGlobal('axios');
    const [aktiv, setAktiv] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = () => {
        if (!axios) return;
        setLoading(true);
        axios.get('inventur/aktiv').then(r => {
            setAktiv(r.data && r.data._id ? r.data : null);
            setLoading(false);
        });
    };

    useEffect(() => { reload(); }, [axios]);

    if (loading) return <Container className="inventur-container"><Typography>lädt...</Typography></Container>;
    if (!aktiv) return <InventurStart onStarted={reload}/>;
    if (aktiv.status === 'in_progress') return <InventurErfassung inventur={aktiv} reload={reload}/>;
    if (aktiv.status === 'pending_confirmation') return <InventurVorschau inventur={aktiv} reload={reload}/>;
    return <Container className="inventur-container"><Typography>Unbekannter Status: {aktiv.status}</Typography></Container>;
}

function InventurStart({onStarted}) {
    const [axios] = useGlobal('axios');
    const [historie, setHistorie] = useState([]);

    useEffect(() => {
        if (!axios) return;
        axios.get('inventur/historie').then(r => setHistorie(r.data || []));
    }, [axios]);

    const startNew = () => {
        axios.post('inventur/start').then(() => onStarted());
    };

    return (
        <Container className="inventur-container">
            <Typography variant="h5">Inventur</Typography>
            <Box>
                <Button variant="contained" onClick={startNew}>Neue Inventur starten</Button>
            </Box>
            <Typography variant="h6">Vergangene Inventuren</Typography>
            {historie.length === 0 && <Typography>Bisher keine Inventuren erfasst.</Typography>}
            {historie.length > 0 && (
                <table className="inventur-tabelle">
                    <thead>
                        <tr><th>Start</th><th>Operator</th><th>Status</th><th>Items</th></tr>
                    </thead>
                    <tbody>
                        {historie.map(h => (
                            <tr key={h._id}>
                                <td>{h.startedAt}</td>
                                <td>{h.startedBy}</td>
                                <td>{h.status}</td>
                                <td>{h.items?.length ?? 0}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Container>
    );
}

function InventurErfassung({inventur, reload}) {
    const [axios] = useGlobal('axios');
    const [werte, setWerte] = useState(() => {
        const initial = {};
        for (const it of inventur.items || []) {
            initial[`${it.produkt_id}|${it.variante}`] = it.ist;
        }
        return initial;
    });
    const [druck, setDruck] = useState(false);
    const timers = useRef({});

    const saveWert = (item, val) => {
        const num = val === '' || val === null ? null : Number(val);
        axios.post('inventur/erfassen', {
            produkt_id: item.produkt_id,
            variante: item.variante,
            ist: Number.isNaN(num) ? null : num,
        });
    };

    const onChange = (item, val) => {
        const key = `${item.produkt_id}|${item.variante}`;
        setWerte(w => ({...w, [key]: val}));
        if (timers.current[key]) clearTimeout(timers.current[key]);
        timers.current[key] = setTimeout(() => saveWert(item, val), 500);
    };

    const onVorschau = () => {
        axios.post('inventur/vorschau').then(() => reload());
    };

    const onAbbrechen = () => {
        if (!window.confirm('Inventur wirklich abbrechen?')) return;
        axios.post('inventur/abbrechen').then(() => reload());
    };

    if (druck) return <InventurDruck inventur={inventur} onClose={() => setDruck(false)}/>;

    return (
        <Container className="inventur-container">
            <Typography variant="h5">Inventur-Erfassung</Typography>
            <Box sx={{display:'flex', gap:2}}>
                <Button variant="outlined" onClick={() => setDruck(true)}>Druckansicht</Button>
                <Button variant="contained" onClick={onVorschau}>Vorschau berechnen</Button>
                <Button variant="text" color="error" onClick={onAbbrechen}>Inventur abbrechen</Button>
            </Box>
            <table className="inventur-tabelle">
                <thead>
                    <tr><th>Produkt</th><th>Variante</th><th>Soll</th><th>Ist</th></tr>
                </thead>
                <tbody>
                    {inventur.items.map(it => {
                        const key = `${it.produkt_id}|${it.variante}`;
                        const val = werte[key];
                        const unfilled = val === null || val === undefined || val === '';
                        return (
                            <tr key={key} className={unfilled ? 'unfilled' : ''}>
                                <td>{it.produkt_name}</td>
                                <td>{it.variante || '—'}</td>
                                <td>{it.soll}</td>
                                <td><TextField size="small" type="number" className="ist-feld"
                                    value={val ?? ''}
                                    onChange={e => onChange(it, e.target.value)}/></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </Container>
    );
}

function InventurVorschau({inventur, reload}) {
    const [axios] = useGlobal('axios');

    const onBestaetigen = () => {
        if (!window.confirm('Inventur wirklich buchen? Korrekturen sind nicht rückgängig zu machen.')) return;
        axios.post('inventur/bestaetigen').then(() => reload());
    };

    const onZurueck = () => {
        if (!window.confirm('Zurueck zur Erfassung erfordert einen Inventur-Neustart. Stattdessen abbrechen?')) return;
        axios.post('inventur/abbrechen').then(() => reload());
    };

    const onAbbrechen = () => {
        if (!window.confirm('Inventur wirklich abbrechen?')) return;
        axios.post('inventur/abbrechen').then(() => reload());
    };

    const nichtErfasst = (inventur.diffs || []).filter(d => d.ist === null);

    return (
        <Container className="inventur-container">
            <Typography variant="h5">Inventur-Vorschau</Typography>
            {nichtErfasst.length > 0 && (
                <Box sx={{padding:2, backgroundColor:'#fff3cd', border:'1px solid #ffeaa7'}}>
                    <Typography>{nichtErfasst.length} Items wurden nicht erfasst und werden als 0 (Schwund) gebucht.</Typography>
                </Box>
            )}
            <table className="inventur-tabelle">
                <thead>
                    <tr>
                        <th>Produkt</th><th>Variante</th><th>Soll</th><th>Ist</th><th>Diff</th><th>Aktion</th>
                    </tr>
                </thead>
                <tbody>
                    {(inventur.diffs || []).filter(d => d.diff !== 0 || d.ist === null).map(d => (
                        <tr key={`${d.produkt_id}|${d.variante}`} className={d.ist === null ? 'unfilled' : ''}>
                            <td>{d.produkt_name}</td>
                            <td>{d.variante || '—'}</td>
                            <td>{d.soll}</td>
                            <td>{d.ist === null ? '—' : d.ist}</td>
                            <td>{d.diff}</td>
                            <td>
                                {d.aktionen.pool_loeschen > 0 && <div>{d.aktionen.pool_loeschen} POOL löschen</div>}
                                {d.aktionen.reserviert_zuruecksetzen?.length > 0 && <div>{d.aktionen.reserviert_zuruecksetzen.length} reservierte zurückstellen</div>}
                                {d.aktionen.neu_anlegen > 0 && <div>{d.aktionen.neu_anlegen} POOL neu anlegen</div>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <Box sx={{display:'flex', gap:2}}>
                <Button variant="contained" color="primary" onClick={onBestaetigen}>Bestätigen und buchen</Button>
                <Button variant="outlined" onClick={onZurueck}>Zurück zur Erfassung (Abbruch)</Button>
                <Button variant="text" color="error" onClick={onAbbrechen}>Inventur abbrechen</Button>
            </Box>
        </Container>
    );
}

function escapeHtml(str) {
    return String(str ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}

function InventurDruck({inventur, onClose}) {
    useEffect(() => {
        const printWindow = window.open('', '_blank', 'width=900,height=700');
        if (!printWindow) {
            alert('Druck-Fenster blockiert. Bitte Pop-ups für diese Seite erlauben.');
            onClose();
            return;
        }
        const rows = inventur.items.map(it => (
            `<tr>
                <td>${escapeHtml(it.produkt_name)}</td>
                <td>${it.variante ? escapeHtml(it.variante) : '—'}</td>
                <td>${it.soll}</td>
                <td class="ist-spalte"></td>
                <td class="notiz-spalte"></td>
            </tr>`
        )).join('');
        const html = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<title>Inventurliste vom ${escapeHtml(inventur.startedAt)}</title>
<style>
  body { font-family: sans-serif; margin: 20px; }
  h1 { font-size: 16pt; margin: 0 0 8px; }
  p { margin: 0 0 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 11pt; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #000; padding: 4px 8px; text-align: left; vertical-align: middle; }
  th { background: #eee; }
  .ist-spalte, .notiz-spalte { min-width: 80px; }
  @media print { body { margin: 10mm; } }
</style>
</head>
<body>
<h1>Inventurliste vom ${escapeHtml(inventur.startedAt)}</h1>
<p>Erfasst durch: ${escapeHtml(inventur.startedBy)}</p>
<table>
<thead><tr><th>Produkt</th><th>Variante</th><th>Soll</th><th class="ist-spalte">Ist</th><th class="notiz-spalte">Notiz</th></tr></thead>
<tbody>${rows}</tbody>
</table>
<script>
window.onload = function() {
  setTimeout(function() { window.print(); }, 100);
};
</script>
</body>
</html>`;
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        onClose();
    }, [inventur, onClose]);

    return null;
}
